<script lang="ts" setup>
	import { uccCompanyHeaderOptions, type UccSearchBy } from "@/utils/uccCompanySearch";
	import { isIndustryClassificationField } from "@/utils/industryCodeFilters";
	import { storeToRefs } from "pinia";
	import { toast } from "vue-sonner";
	import { useUser } from "~/stores/user";
	import { getHostUrl } from "@/composables/store";
	import { showModal } from "@/services/crmModalService";
	import { DropDownOption } from "@/utils/types";
	import { AccountMenuSection, getAccountMenuSections } from "@/utils/accountMenu";
	import { useIsPhone } from "@/composables/useIsPhone";
	import { useMobileMenu } from "@/composables/useMobileMenu";
	import { useLayoutPreference } from "@/composables/useLayoutPreference";
	import { buildSavedSearchListCsv, buildSavedSearchListFilename } from "@/utils/savedSearchListCsv";
	import {
		clearCompanySearchHistory,
		companySearchPayloadTooltip,
		deleteCompanySearchHistoryEntry,
		fetchCompanySearchHistory,
		summarizeCompanySearchPayload,
		COMPANY_HISTORY_APPLY_STATE_KEY,
		type CompanyHistoryApplyRequest,
		type CompanySearchHistoryEntry,
	} from "@/utils/companySearchHistory";
	import { summarizeUccPayload, uccPayloadTooltip } from "@/utils/uccPayloadSummary";
	import {
		clearUccSearchHistory,
		deleteUccSearchHistoryEntry,
		fetchUccSearchHistory,
		UCC_HISTORY_APPLY_STATE_KEY,
		type UccHistoryApplyRequest,
		type UccSearchHistoryEntry,
	} from "@/utils/uccSearchHistory";
	import {
		fetchUccSearchSuggestions,
		formatUccSuggestionDescription,
		normalizeUccSuggestionState,
		UCC_SUGGEST_DEBOUNCE_MS,
		UCC_SUGGEST_LIMIT,
		uccSuggestionsSupported,
		uccSuggestQueryFromInput,
		uccValuesWithSuggestion,
		type UccSearchSuggestion,
	} from "@/utils/uccSearchSuggest";

	const route = useRoute();
	const router = useRouter();

	const userStore = useUser();
	const { user } = storeToRefs(userStore);

	const hasProfilePicture = computed(() => {
		const profilePicture = user.value?.profile_picture;
		if (!profilePicture) return false;
		if (typeof profilePicture === "string" && profilePicture.toLowerCase() === "none") return false;
		return true;
	});

	// Phone chrome. The hover account menu is replaced by the menu sheet a tap
	// on the avatar opens. No brand mark: the tab bar carries the app identity,
	// and the search field or title starts on the same 16px gutter as the page.
	const isPhone = useIsPhone();
	const mobileMenu = useMobileMenu();
	// A phone showing the desktop layout gets a way back in the account menu,
	// since the phone tab bar and menu sheet are gone in that layout.
	const layoutPreference = useLayoutPreference();
	const userFullName = computed(() => `${user.value?.first_name ?? ""} ${user.value?.last_name ?? ""}`.trim() || "User");

	function onProfileClick() {
		if (!isPhone.value) return;
		mobileMenu.show();
	}

	const title = computed(() => {
		const normalizedPath = route.path.replace(/\/$/, "");

		if (normalizedPath.startsWith("/ucc/secured-party/")) {
			return "Secured Party Overview";
		} else if (/^\/oauth\/[^/]+\/callback$/.test(normalizedPath)) {
			return "CRM Integration";
		} else if (route.path.includes("/ucc/recommendations")) {
			return "UCC Recommendations";
		} else if (route.path.includes("/ucc/saved-companies")) {
			return "Saved UCC Companies and Filings";
		} else if (route.path.includes("companies")) {
			return "Companies";
		} else if (route.path.includes("contacts")) {
			return "Contacts";
		} else if (route.path.includes("enrichment")) {
			return "Enrichment";
		} else if (route.path.includes("exports")) {
			return "Exports";
		} else if (route.path.includes("/ucc/reports")) {
			return "UCC scheduled reports";
		} else if (route.path.includes("/ucc/monitoring")) {
			return "UCC Company Monitoring";
		} else if (route.path.includes("/ucc/company")) {
			return "UCC Company Overview";
		} else if (route.path.includes("support")) {
			return "Help & Support";
		} else if (route.path.includes("account/profile")) {
			return "Profile & Billing";
		} else if (route.path.includes("account/users")) {
			return "User Management";
		} else if (route.path.includes("account/api")) {
			return "API";
		} else if (normalizedPath === "/account/crm") {
			return "CRM Integration";
		} else if (route.path.includes("account/ucc-field-mappings")) {
			return "UCC Export Fields";
		} else if (normalizedPath === "/account/crm-settings") {
			return "CRM Field Mappings";
		} else if (route.path.includes("account/credit-history")) {
			return "Credit History";
		} else if (route.path.includes("account/credits")) {
			return "Get Credits";
		} else if (route.path.includes("account")) {
			return "Account";
		}

		return route.name;
	});

	const showCompaniesSearch = computed(() => /^\/companies(?:\/|$)/.test(route.path));
	const showContactsSearch = computed(() => route.path.includes("contacts"));
	const showUccSearch = computed(() => /^\/ucc\/?$/.test(route.path));

	type UccSearchMode = UccSearchBy;
	type SavedDebtorSearchField =
		| "name"
		| "url"
		| "phone"
		| "address"
		| "industry"
		| "naics_code"
		| "naics_description"
		| "eight_digit_sic_code"
		| "eight_digit_sic_description"
		| "four_digit_sic_code"
		| "four_digit_sic_description"
		| "two_digit_sic_code"
		| "two_digit_sic_description";
	type SavedSecuredPartySearchField =
		| "name"
		| "url"
		| "secured_party_industry"
		| "secured_party_naics_code"
		| "secured_party_naics_description"
		| "secured_party_eight_digit_sic_code"
		| "secured_party_eight_digit_sic_description"
		| "secured_party_four_digit_sic_code"
		| "secured_party_four_digit_sic_description"
		| "secured_party_two_digit_sic_code"
		| "secured_party_two_digit_sic_description";
	type UccSavedDebtorSearchList = {
		record_id: string;
		name: string;
		source_filename?: string;
		value_counts: Partial<Record<SavedDebtorSearchField, number>>;
		values_by_field?: Partial<Record<SavedDebtorSearchField, string[]>>;
		total_values: number;
	};
	type UccSavedSecuredPartySearchList = {
		record_id: string;
		name: string;
		source_filename?: string;
		value_counts: Partial<Record<SavedSecuredPartySearchField, number>>;
		values_by_field?: Partial<Record<SavedSecuredPartySearchField, string[]>>;
		total_values: number;
	};
	type UccSavedDebtorSearchSelection = {
		list_id: string;
		list_name: string;
		field: SavedDebtorSearchField;
		field_label: string;
		value_count: number;
		values_by_field?: Partial<Record<SavedDebtorSearchField, string[]>>;
		selected_at: number;
	};
	type UccSavedSecuredPartySearchSelection = {
		list_id: string;
		list_name: string;
		field: SavedSecuredPartySearchField;
		field_label: string;
		value_count: number;
		values_by_field?: Partial<Record<SavedSecuredPartySearchField, string[]>>;
		selected_at: number;
	};
	type SearchSubmitPayload = {
		value?: string;
		values?: string[];
		state?: string | null;
	};
	type CompanyHeaderSearchSubmit = {
		sequence: number;
		query?: string;
		value?: string;
		competitor_lookalike?: string;
		values: string[];
		submittedAt: number;
	};
	type UccHeaderSearchSubmit = {
		sequence: number;
		mode?: UccSearchMode;
		field: string;
		values: string[];
		state?: string;
		submittedAt: number;
	};

	// Share search values so header and pages stay in sync.
	const companySearchValue = useState<string>("company-search-value", () => "");
	const companySearchValues = useState<string[]>("company-search-values", () => []);
	const companyCompetitorLookalike = useState<boolean>("company-competitor-lookalike", () => false);
	const companyHeaderSearchSubmitSequence = useState<number>("company-header-search-submit-sequence", () => 0);
	const companyHeaderSearchSubmit = useState<CompanyHeaderSearchSubmit | null>("company-header-search-submit", () => null);
	const companySearchResultId = useState<string | null>("company-search-result-id", () => null);
	const companySearchHistory = useState<CompanySearchHistoryEntry[]>("company-search-history", () => []);
	const companyHistoryApplySequence = useState<number>("company-history-apply-sequence", () => 0);
	const companyHistoryApplyRequest = useState<CompanyHistoryApplyRequest | null>(COMPANY_HISTORY_APPLY_STATE_KEY, () => null);
	const contactSearchValue = ref<string>("");
	const uccDebtorSearchValue = useState<string>("ucc-debtor-search-value", () => "");
	const uccDebtorSearchValues = useState<string[]>("ucc-debtor-search-values", () => []);
	const uccSecuredPartySearchValue = useState<string>("ucc-secured-party-search-value", () => "");
	const uccSecuredPartySearchValues = useState<string[]>("ucc-secured-party-search-values", () => []);
	const uccCompanySearchValue = useState<string>("ucc-company-search-value", () => "");
	const uccCompanySearchValues = useState<string[]>("ucc-company-search-values", () => []);
	const uccSearchField = useState<string>("ucc-search-field", () => "name");
	const uccSearchMode = useState<UccSearchMode>("ucc-search-mode", () => "debtor");
	watch(() => route.query.mode, (mode) => {
		if (route.path === "/ucc" && (mode === "company" || mode === "debtor" || mode === "secured_party")) uccSearchMode.value = mode;
	}, { immediate: true });
	const uccLastSearchPayload = useState<Record<string, unknown> | null>("ucc-last-search-payload", () => null);
	const uccHeaderSearchSubmitSequence = useState<number>("ucc-header-search-submit-sequence", () => 0);
	const uccHeaderSearchSubmit = useState<UccHeaderSearchSubmit | null>("ucc-header-search-submit", () => null);
	const uccSavedDebtorSearchSelection = useState<UccSavedDebtorSearchSelection | null>("ucc-saved-debtor-search-selection", () => null);
	const uccSavedSecuredPartySearchSelection = useState<UccSavedSecuredPartySearchSelection | null>("ucc-saved-secured-party-search-selection", () => null);
	const savedSearchUploadInput = ref<HTMLInputElement | null>(null);
	const savedDebtorSearchLists = ref<UccSavedDebtorSearchList[]>([]);
	const savedSecuredPartySearchLists = ref<UccSavedSecuredPartySearchList[]>([]);
	const savedSearchListsLoading = ref(false);
	const savedSearchUploadLoading = ref(false);
	const savedSearchDownloadLoadingId = ref<string | null>(null);
	const savedSearchDeleteLoadingId = ref<string | null>(null);
	const uccSearchHistory = useState<UccSearchHistoryEntry[]>("ucc-search-history", () => []);
	const uccHistoryApplySequence = useState<number>("ucc-history-apply-sequence", () => 0);
	const uccHistoryApplyRequest = useState<UccHistoryApplyRequest | null>(UCC_HISTORY_APPLY_STATE_KEY, () => null);

	const UCC_HEADER_SEARCH_LIMIT = 20;
	const UCC_SUGGEST_CACHE_LIMIT = 200;

	// Name completions for the UCC search bar, fetched as the person types.
	const uccSuggestions = ref<UccSearchSuggestion[]>([]);
	const uccSuggestionsLoading = ref(false);
	const uccSuggestionsRanked = ref(true);
	const uccSuggestionCache = new Map<string, { suggestions: UccSearchSuggestion[]; ranked: boolean }>();
	let uccSuggestTimer: ReturnType<typeof setTimeout> | null = null;
	let uccSuggestAbort: AbortController | null = null;
	let uccSuggestSequence = 0;

	const {
		loading: companySearchHistoryLoading,
		loadedAt: companySearchHistoryLoadedAt,
		pastThreeDays: companyHistoryPastThreeDays,
		visibleEntries: companyHistoryVisibleEntries,
		load: loadCompanySearchHistory,
		changeRange: changeCompanyHistoryRange,
	} = useRecentSearchHistory(companySearchHistory, fetchCompanySearchHistory, (error, expanding) => {
		console.error("Unable to load Companies search history", error);
		if (expanding) toast.error("Unable to load older searches. Please try again.");
	});

	const {
		loading: uccSearchHistoryLoading,
		loadedAt: uccSearchHistoryLoadedAt,
		pastThreeDays: uccHistoryPastThreeDays,
		visibleEntries: uccHistoryVisibleEntries,
		load: loadUccSearchHistory,
		changeRange: changeUccHistoryRange,
	} = useRecentSearchHistory(uccSearchHistory, fetchUccSearchHistory, (error, expanding) => {
		console.error("Unable to load UCC search history", error);
		if (expanding) toast.error("Unable to load older searches. Please try again.");
	});

	function formatSearchHistoryRelativeTime(value: string) {
		const timestamp = new Date(value).getTime();
		if (!Number.isFinite(timestamp)) return "";
		const elapsedMs = Date.now() - timestamp;
		if (elapsedMs < 60_000) return "just now";
		const minutes = Math.floor(elapsedMs / 60_000);
		if (minutes < 60) return `${minutes}m ago`;
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return `${hours}h ago`;
		const days = Math.floor(hours / 24);
		if (days < 30) return `${days}d ago`;
		return new Date(timestamp).toLocaleDateString();
	}

	const companyHistoryDropdownOptions = computed(() => {
		return companyHistoryVisibleEntries.value.map((entry) => {
			const isLookalike = entry.payload.competitor_lookalike === true || entry.payload.competitor_lookalike === "true" || entry.payload.competitor_lookalike === "1";
			const summary = summarizeCompanySearchPayload(entry.payload) || "All filters";
			const modeLabel = isLookalike ? "Lookalike" : "Keyword";
			const relativeTime = formatSearchHistoryRelativeTime(entry.searchedAt);
			return {
				value: entry.id,
				label: summary,
				description: relativeTime ? `${modeLabel} · ${relativeTime}` : modeLabel,
				title: companySearchPayloadTooltip(`${modeLabel} search`, entry.payload),
			};
		});
	});

	const uccHistoryDropdownOptions = computed(() => {
		return uccHistoryVisibleEntries.value.map((entry) => {
			const summary = summarizeUccPayload(entry.payload, entry.mode) || "All filters";
			const modeLabel = entry.mode === "secured_party" ? "Secured Party" : "Debtor";
			const relativeTime = formatSearchHistoryRelativeTime(entry.searchedAt);
			return {
				value: entry.id,
				label: summary,
				description: relativeTime ? `${modeLabel} · ${relativeTime}` : modeLabel,
				title: uccPayloadTooltip(`${modeLabel} search`, entry.payload, entry.mode),
			};
		});
	});

	function onCompanySearchFocus() {
		if (!showCompaniesSearch.value) return;
		void loadCompanySearchHistory();
	}

	function onCompanyHistorySelect(id: string) {
		const entry = companySearchHistory.value.find((item) => item.id === id);
		if (!entry) return;
		const sequence = companyHistoryApplySequence.value + 1;
		companyHistoryApplySequence.value = sequence;
		companyHistoryApplyRequest.value = {
			sequence,
			payload: entry.payload,
			requestedAt: Date.now(),
		};
	}

	async function onCompanyHistoryDelete(id: string) {
		try {
			await deleteCompanySearchHistoryEntry(id);
			companySearchHistory.value = companySearchHistory.value.filter((item) => item.id !== id);
		} catch (error: any) {
			toast.error(error?.message || "Unable to remove this search from history.");
		}
	}

	async function onCompanyHistoryClear() {
		try {
			await clearCompanySearchHistory();
			companySearchHistory.value = [];
		} catch (error: any) {
			toast.error(error?.message || "Unable to clear search history.");
		}
	}

	function onUccSearchFocus() {
		if (!showUccSearch.value || uccSearchMode.value === "company") return;
		void loadUccSearchHistory();
	}

	function onUccHistorySelect(id: string) {
		const entry = uccSearchHistory.value.find((item) => item.id === id);
		if (!entry) return;
		const sequence = uccHistoryApplySequence.value + 1;
		uccHistoryApplySequence.value = sequence;
		uccHistoryApplyRequest.value = {
			sequence,
			mode: entry.mode,
			payload: entry.payload,
			requestedAt: Date.now(),
		};
	}

	async function onUccHistoryDelete(id: string) {
		try {
			await deleteUccSearchHistoryEntry(id);
			uccSearchHistory.value = uccSearchHistory.value.filter((item) => item.id !== id);
		} catch (error: any) {
			toast.error(error?.message || "Unable to remove this search from history.");
		}
	}

	async function onUccHistoryClear() {
		try {
			await clearUccSearchHistory();
			uccSearchHistory.value = [];
		} catch (error: any) {
			toast.error(error?.message || "Unable to clear search history.");
		}
	}

	const uccSearchValue = computed<string>({
		get() {
			if (uccSearchMode.value === "company") return uccCompanySearchValue.value;
			return uccSearchMode.value === "secured_party" ? uccSecuredPartySearchValue.value : uccDebtorSearchValue.value;
		},
		set(value) {
			if (uccSearchMode.value === "company") { uccCompanySearchValue.value = value; return; }
			if (uccSearchMode.value === "secured_party") {
				uccSecuredPartySearchValue.value = value;
				return;
			}
			uccDebtorSearchValue.value = value;
		},
	});

	const uccSearchValues = computed<string[]>({
		get() {
			if (uccSearchMode.value === "company") return uccCompanySearchValues.value;
			return uccSearchMode.value === "secured_party" ? uccSecuredPartySearchValues.value : uccDebtorSearchValues.value;
		},
		set(values) {
			if (uccSearchMode.value === "company") { uccCompanySearchValues.value = values; return; }
			if (uccSearchMode.value === "secured_party") {
				uccSecuredPartySearchValues.value = values;
				return;
			}
			uccDebtorSearchValues.value = values;
		},
	});

	// Only the party-name field has an index of whole names to complete from.
	const uccSuggestionsEnabled = computed(() => showUccSearch.value && uccSuggestionsSupported(uccSearchField.value));

	const uccSuggestionEmptyLabel = computed(() => {
		if (uccSearchMode.value === "company") return "No matching companies";
		return uccSearchMode.value === "secured_party" ? "No matching secured parties" : "No matching debtors";
	});

	const uccSuggestionDropdownOptions = computed(() => {
		const modeLabel = uccSearchMode.value === "company" ? "company" : uccSearchMode.value === "secured_party" ? "secured party" : "debtor";
		return uccSuggestions.value.map((suggestion) => ({
			value: suggestion.value,
			label: suggestion.label,
			state: suggestion.state,
			// An unranked source has no trustworthy filing count to show.
			description: formatUccSuggestionDescription(uccSuggestionsRanked.value ? suggestion : { ...suggestion, filings: null }),
			title: `Search ${modeLabel}${uccSearchMode.value === "company" ? "" : " filings"} for ${suggestion.value}`,
		}));
	});

	function clearUccSuggestions() {
		if (uccSuggestTimer) {
			clearTimeout(uccSuggestTimer);
			uccSuggestTimer = null;
		}
		uccSuggestAbort?.abort();
		uccSuggestAbort = null;
		uccSuggestSequence += 1;
		uccSuggestions.value = [];
		uccSuggestionsLoading.value = false;
	}

	function rememberUccSuggestions(key: string, entry: { suggestions: UccSearchSuggestion[]; ranked: boolean }) {
		uccSuggestionCache.set(key, entry);
		if (uccSuggestionCache.size > UCC_SUGGEST_CACHE_LIMIT) {
			const oldest = uccSuggestionCache.keys().next().value;
			if (oldest !== undefined) uccSuggestionCache.delete(oldest);
		}
	}

	async function loadUccSuggestions(mode: UccSearchMode, q: string) {
		const key = `${mode}:${q.toLowerCase()}`;
		const cached = uccSuggestionCache.get(key);
		if (cached) {
			uccSuggestions.value = cached.suggestions;
			uccSuggestionsRanked.value = cached.ranked;
			uccSuggestionsLoading.value = false;
			return;
		}
		uccSuggestAbort?.abort();
		const controller = new AbortController();
		uccSuggestAbort = controller;
		const sequence = ++uccSuggestSequence;
		uccSuggestionsLoading.value = true;
		try {
			const result = await fetchUccSearchSuggestions(mode === "company" ? "debtor" : mode, q, { limit: UCC_SUGGEST_LIMIT, signal: controller.signal });
			if (sequence !== uccSuggestSequence) return;
			rememberUccSuggestions(key, { suggestions: result.suggestions, ranked: result.ranked });
			uccSuggestions.value = result.suggestions;
			uccSuggestionsRanked.value = result.ranked;
		} catch (error: any) {
			if (controller.signal.aborted || sequence !== uccSuggestSequence) return;
			// Suggestions are a convenience; typing and submitting keep working without them.
			console.error("Unable to load UCC search suggestions", error);
			uccSuggestions.value = [];
		} finally {
			if (sequence === uccSuggestSequence) uccSuggestionsLoading.value = false;
		}
	}

	function scheduleUccSuggestions() {
		if (uccSuggestTimer) {
			clearTimeout(uccSuggestTimer);
			uccSuggestTimer = null;
		}
		if (!uccSuggestionsEnabled.value) {
			clearUccSuggestions();
			return;
		}
		const q = uccSuggestQueryFromInput(uccSearchValue.value);
		if (!q) {
			clearUccSuggestions();
			return;
		}
		const mode = uccSearchMode.value;
		uccSuggestTimer = setTimeout(() => {
			uccSuggestTimer = null;
			void loadUccSuggestions(mode, q);
		}, UCC_SUGGEST_DEBOUNCE_MS);
	}

	watch([uccSearchValue, uccSearchMode, uccSuggestionsEnabled], scheduleUccSuggestions);

	onBeforeUnmount(() => {
		clearUccSuggestions();
	});

	function onUccSuggestionSelect(selectedValue: string, selectedState?: string | null) {
		// Entries already separated with ";" stay; only the one being typed is replaced.
		const typed = splitSearchValues(uccSearchValue.value);
		const committed = uccSuggestQueryFromInput(uccSearchValue.value) ? typed.slice(0, -1) : typed;
		const values = uccValuesWithSuggestion([...uccSearchValues.value, ...committed], selectedValue);
		clearUccSuggestions();
		runUccSearch({ value: "", values, state: selectedState });
	}

	const debtorSearchFields = [
		"name",
		"url",
		"phone",
		"address",
		"industry",
		"naics_code",
		"naics_description",
		"eight_digit_sic_code",
		"eight_digit_sic_description",
		"four_digit_sic_code",
		"four_digit_sic_description",
		"two_digit_sic_code",
		"two_digit_sic_description",
	] as const;

	const securedPartySearchFields = [
		"name",
		"url",
		"secured_party_industry",
		"secured_party_naics_code",
		"secured_party_naics_description",
		"secured_party_eight_digit_sic_code",
		"secured_party_eight_digit_sic_description",
		"secured_party_four_digit_sic_code",
		"secured_party_four_digit_sic_description",
		"secured_party_two_digit_sic_code",
		"secured_party_two_digit_sic_description",
	] as const;

	const uccDebtorDropdownOptions = [
		{ id: "name", value: "name", label: "Debtor Name", selected: false },
		{ id: "url", value: "url", label: "Debtor URL", selected: false },
		{ id: "phone", value: "phone", label: "Debtor Phone Number", selected: false },
		{ id: "address", value: "address", label: "Debtor Address", selected: false },
		{ id: "industry", value: "industry", label: "Debtor Industry", selected: false },
		{ id: "naics_code", value: "naics_code", label: "Debtor NAICS Code", selected: false },
		{ id: "naics_description", value: "naics_description", label: "Debtor NAICS Description", selected: false },
		{ id: "eight_digit_sic_code", value: "eight_digit_sic_code", label: "Debtor SIC 8-Digit Code", selected: false },
		{ id: "eight_digit_sic_description", value: "eight_digit_sic_description", label: "Debtor SIC 8-Digit Description", selected: false },
		{ id: "four_digit_sic_code", value: "four_digit_sic_code", label: "Debtor SIC 4-Digit Code", selected: false },
		{ id: "four_digit_sic_description", value: "four_digit_sic_description", label: "Debtor SIC 4-Digit Description", selected: false },
		{ id: "two_digit_sic_code", value: "two_digit_sic_code", label: "Debtor SIC 2-Digit Code", selected: false },
		{ id: "two_digit_sic_description", value: "two_digit_sic_description", label: "Debtor SIC 2-Digit Description", selected: false },
	] as const satisfies readonly DropDownOption[];

	const uccSecuredPartyDropdownOptions = [
		{ id: "name", value: "name", label: "Secured Party Name", selected: false },
		{ id: "url", value: "url", label: "Secured Party URL", selected: false },
		{ id: "secured_party_industry", value: "secured_party_industry", label: "Secured Party Industry", selected: false },
		{ id: "secured_party_naics_code", value: "secured_party_naics_code", label: "Secured Party NAICS Code", selected: false },
		{ id: "secured_party_naics_description", value: "secured_party_naics_description", label: "Secured Party NAICS Description", selected: false },
		{ id: "secured_party_eight_digit_sic_code", value: "secured_party_eight_digit_sic_code", label: "Secured Party SIC 8-Digit Code", selected: false },
		{ id: "secured_party_eight_digit_sic_description", value: "secured_party_eight_digit_sic_description", label: "Secured Party SIC 8-Digit Description", selected: false },
		{ id: "secured_party_four_digit_sic_code", value: "secured_party_four_digit_sic_code", label: "Secured Party SIC 4-Digit Code", selected: false },
		{ id: "secured_party_four_digit_sic_description", value: "secured_party_four_digit_sic_description", label: "Secured Party SIC 4-Digit Description", selected: false },
		{ id: "secured_party_two_digit_sic_code", value: "secured_party_two_digit_sic_code", label: "Secured Party SIC 2-Digit Code", selected: false },
		{ id: "secured_party_two_digit_sic_description", value: "secured_party_two_digit_sic_description", label: "Secured Party SIC 2-Digit Description", selected: false },
	] as const satisfies readonly DropDownOption[];

	const uccDropdownOptions = ref<DropDownOption[]>([]);
	const debtorSearchFieldLabels = Object.fromEntries(uccDebtorDropdownOptions.map((option) => [option.value, option.label])) as Record<SavedDebtorSearchField, string>;
	const securedPartySearchFieldLabels = Object.fromEntries(uccSecuredPartyDropdownOptions.map((option) => [option.value, option.label])) as Record<SavedSecuredPartySearchField, string>;

	const savedSearchUploadFieldLabel = computed(() => {
		const field = uccSearchField.value;
		if (uccSearchMode.value === "secured_party") {
			return isSavedSecuredPartySearchField(field) ? securedPartySearchFieldLabels[field] : "Secured Party Name";
		}
		return isSavedDebtorSearchField(field) ? debtorSearchFieldLabels[field] : "Debtor Name";
	});

	const savedSearchUploadActionLabel = computed(() => {
		const fieldLabel = savedSearchUploadFieldLabel.value;
		return savedSearchUploadLoading.value ? `Uploading ${fieldLabel} list...` : `Upload ${fieldLabel} list`;
	});

	const savedDebtorDropdownOptions = computed<DropDownOption[]>(() => {
		if (uccSearchMode.value !== "debtor") return [];

		return savedDebtorSearchLists.value.flatMap((list) => {
			return (Object.entries(list.value_counts || {}) as [SavedDebtorSearchField, number][])
				.filter(([field, count]) => debtorSearchFields.includes(field) && Number(count) > 0)
				.map(([field, count]) => ({
					id: `${list.record_id}:${field}`,
					value: `${list.record_id}::${field}`,
					label: `${list.name} - ${debtorSearchFieldLabels[field]} (${Number(count).toLocaleString()})`,
					selected: false,
				}));
		});
	});

	const savedSecuredPartyDropdownOptions = computed<DropDownOption[]>(() => {
		if (uccSearchMode.value !== "secured_party") return [];

		return savedSecuredPartySearchLists.value.flatMap((list) => {
			return (Object.entries(list.value_counts || {}) as [SavedSecuredPartySearchField, number][])
				.filter(([field, count]) => securedPartySearchFields.includes(field) && Number(count) > 0)
				.map(([field, count]) => ({
					id: `${list.record_id}:${field}`,
					value: `${list.record_id}::${field}`,
					label: `${list.name} - ${securedPartySearchFieldLabels[field]} (${Number(count).toLocaleString()})`,
					selected: false,
				}));
		});
	});

	const savedSearchDropdownOptions = computed<DropDownOption[]>(() => {
		return uccSearchMode.value === "secured_party" ? savedSecuredPartyDropdownOptions.value : savedDebtorDropdownOptions.value;
	});

	const savedSearchValuesLabel = computed(() => {
		if (uccSearchMode.value === "company") return "";
		const selection = uccSearchMode.value === "secured_party" ? uccSavedSecuredPartySearchSelection.value : uccSavedDebtorSearchSelection.value;
		if (!selection) return "";
		if (selection.field !== uccSearchField.value) return "";
		return `${selection.list_name} - ${selection.field_label} (${selection.value_count.toLocaleString()})`;
	});

	const uccSearchPlaceholder = computed(() => {
		// The mode chip beside the field already says "Debtor Name" or "Secured
		// Party"; at phone width the long prompt only truncated.
		if (isPhone.value) return "Search…";
		if (uccSearchMode.value === "company") return "Search UCC companies…";
		return uccSearchMode.value === "secured_party" ? "Search UCC secured parties…" : "Search UCC debtors…";
	});

	const multiValueSeparators = /[;\n]+/g;
	const explicitOrSeparator = /\s+\bOR\b\s+/gi;

	function normalizeSearchValues(values: string[]) {
		const seen = new Set<string>();
		const next: string[] = [];
		values.forEach((value) => {
			const trimmed = value.trim();
			if (!trimmed) return;
			if (seen.has(trimmed)) return;
			seen.add(trimmed);
			next.push(trimmed);
		});
		return next;
	}

	function splitSearchValues(input: string, splitExplicitOr = false) {
		const separator = splitExplicitOr ? new RegExp(`${multiValueSeparators.source}|${explicitOrSeparator.source}`, "gi") : multiValueSeparators;
		return input
			.split(separator)
			.map((value) => value.trim())
			.filter((value) => value.length > 0);
	}

	function parseQuerySearchValues(raw: string | string[] | null | undefined, splitExplicitOr = false) {
		if (!raw) return [];
		if (Array.isArray(raw)) {
			return normalizeSearchValues(raw.map((value) => String(value)));
		}
		const trimmed = String(raw).trim();
		if (!trimmed) return [];
		if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
			try {
				const parsed = JSON.parse(trimmed);
				if (Array.isArray(parsed)) {
					return normalizeSearchValues(parsed.map((value) => String(value)));
				}
			} catch (error) {
				// fall through to split handling
			}
		}
		return normalizeSearchValues(splitSearchValues(trimmed, splitExplicitOr));
	}

	function collectUccSearchValues(payload?: SearchSubmitPayload) {
		const sourceValue = payload?.value ?? searchValue.value ?? "";
		const sourceValues = payload?.values ?? uccSearchValues.value;
		const inputValues = splitSearchValues(sourceValue.trim());
		return normalizeSearchValues([...sourceValues, ...inputValues]);
	}

	function collectCompanySearchValues(payload?: SearchSubmitPayload) {
		const sourceValue = payload?.value ?? companySearchValue.value ?? "";
		const sourceValues = payload?.values ?? companySearchValues.value;
		const inputValues = splitSearchValues(sourceValue.trim(), true);
		return normalizeSearchValues([...sourceValues, ...inputValues]);
	}

	function serializeCompanySearchValues(values: string[]) {
		return values.length === 1 ? values[0] : JSON.stringify(values);
	}

	function syncUccDropdownOptions() {
		const baseOptions = (uccSearchMode.value === "company" ? uccCompanyHeaderOptions : uccSearchMode.value === "secured_party" ? uccSecuredPartyDropdownOptions : uccDebtorDropdownOptions).filter(
			(option) => !isIndustryClassificationField(option.value) && option.value !== "industry" && option.value !== "secured_party_industry"
		);
		uccDropdownOptions.value = baseOptions.map((opt) => ({
			...opt,
			selected: opt.value === uccSearchField.value,
		}));
	}

	function isSavedSecuredPartySearchField(value: string): value is SavedSecuredPartySearchField {
		return (securedPartySearchFields as readonly string[]).includes(value);
	}

	function isSavedDebtorSearchField(value: string): value is SavedDebtorSearchField {
		return (debtorSearchFields as readonly string[]).includes(value);
	}

	function authHeaders() {
		return {
			Authorization: userStore.token ? `Bearer ${userStore.token}` : "",
		};
	}

	function formatApiErrorValue(value: unknown): string | null {
		if (typeof value === "string") {
			const trimmed = value.trim();
			return trimmed.length > 0 ? trimmed : null;
		}

		if (Array.isArray(value)) {
			const messages = value.map((item) => formatApiErrorValue(item)).filter((item): item is string => Boolean(item));
			return messages.length > 0 ? messages.join("; ") : null;
		}

		if (value && typeof value === "object") {
			const payload = value as Record<string, unknown>;
			const directMessage = formatApiErrorValue(payload.detail) || formatApiErrorValue(payload.error) || formatApiErrorValue(payload.message);
			if (directMessage) return directMessage;

			const validationMessage = formatApiErrorValue(payload.msg);
			if (validationMessage) {
				const location = Array.isArray(payload.loc)
					? payload.loc
							.map((part) => String(part))
							.filter((part) => part && part !== "body")
							.join(".")
					: "";
				return location ? `${location}: ${validationMessage}` : validationMessage;
			}
		}

		return null;
	}

	function extractApiErrorMessage(payload: unknown, fallback: string) {
		return formatApiErrorValue(payload) || fallback;
	}

	async function fetchSavedSecuredPartySearchLists() {
		if (savedSearchListsLoading.value || uccSearchMode.value !== "secured_party") return;
		savedSearchListsLoading.value = true;

		try {
			const response = await fetch(`${getHostUrl()}/ucc/secured_party/saved-search-lists`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					...authHeaders(),
				},
			});
			if (!response.ok) throw new Error(`Request failed (${response.status})`);
			const json = (await response.json()) as { success?: boolean; list?: UccSavedSecuredPartySearchList[] };
			if (!json.success) throw new Error("Saved secured-party lists did not load.");
			savedSecuredPartySearchLists.value = Array.isArray(json.list) ? json.list : [];
		} catch (error) {
			console.error(error);
		} finally {
			savedSearchListsLoading.value = false;
		}
	}

	async function fetchSavedDebtorSearchLists() {
		if (savedSearchListsLoading.value || uccSearchMode.value !== "debtor") return;
		savedSearchListsLoading.value = true;

		try {
			const response = await fetch(`${getHostUrl()}/ucc/debtor/saved-search-lists`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					...authHeaders(),
				},
			});
			if (!response.ok) throw new Error(`Request failed (${response.status})`);
			const json = (await response.json()) as { success?: boolean; list?: UccSavedDebtorSearchList[] };
			if (!json.success) throw new Error("Saved debtor lists did not load.");
			savedDebtorSearchLists.value = Array.isArray(json.list) ? json.list : [];
		} catch (error) {
			console.error(error);
		} finally {
			savedSearchListsLoading.value = false;
		}
	}

	async function fetchSavedSecuredPartySearchListValues(listId: string) {
		const response = await fetch(`${getHostUrl()}/ucc/secured_party/saved-search-lists/${encodeURIComponent(listId)}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				...authHeaders(),
			},
		});
		const json = await response.json().catch(() => ({}));
		if (!response.ok || !json?.success) {
			throw new Error(extractApiErrorMessage(json, "Unable to load saved secured-party list values."));
		}

		const data = json.data as UccSavedSecuredPartySearchList;
		return data.values_by_field || {};
	}

	async function fetchSavedDebtorSearchListValues(listId: string) {
		const response = await fetch(`${getHostUrl()}/ucc/debtor/saved-search-lists/${encodeURIComponent(listId)}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				...authHeaders(),
			},
		});
		const json = await response.json().catch(() => ({}));
		if (!response.ok || !json?.success) {
			throw new Error(extractApiErrorMessage(json, "Unable to load saved debtor list values."));
		}

		const data = json.data as UccSavedDebtorSearchList;
		return data.values_by_field || {};
	}

	function triggerSavedSearchUpload() {
		if (savedSearchUploadLoading.value || uccSearchMode.value === "company") return;
		const input = savedSearchUploadInput.value;
		if (!input) return;
		input.value = "";
		input.click();
	}

	async function uploadSavedSearchFile(event: Event) {
		const input = event.target as HTMLInputElement | null;
		const file = input?.files?.[0];
		if (!file) return;

		savedSearchUploadLoading.value = true;
		try {
			const isSecuredPartyMode = uccSearchMode.value === "secured_party";
			const defaultName = isSecuredPartyMode ? "Secured party saved list" : "Debtor saved list";
			const endpoint = isSecuredPartyMode ? "/ucc/secured_party/saved-search-lists/upload" : "/ucc/debtor/saved-search-lists/upload";
			const selectedField = uccSearchField.value;
			if (isSecuredPartyMode && !isSavedSecuredPartySearchField(selectedField)) {
				throw new Error("Select a secured-party field before uploading a saved list.");
			}
			if (!isSecuredPartyMode && !isSavedDebtorSearchField(selectedField)) {
				throw new Error("Select a debtor field before uploading a saved list.");
			}
			const uploadFieldLabel = isSecuredPartyMode
				? securedPartySearchFieldLabels[selectedField as SavedSecuredPartySearchField]
				: debtorSearchFieldLabels[selectedField as SavedDebtorSearchField];

			const formData = new FormData();
			formData.append("upload", file);
			formData.append(
				"name",
				file.name
					.replace(/\.(csv|xlsx)$/i, "")
					.replace(/[_-]+/g, " ")
					.trim() || defaultName
			);
			formData.append("search_field", selectedField);

			const response = await fetch(`${getHostUrl()}${endpoint}`, {
				method: "POST",
				headers: authHeaders(),
				body: formData,
			});
			const json = await response.json().catch(() => ({}));
			if (!response.ok || !json?.success) {
				throw new Error(extractApiErrorMessage(json, `Unable to upload saved ${isSecuredPartyMode ? "secured-party" : "debtor"} list.`));
			}

			if (isSecuredPartyMode) {
				const savedList = json.data as UccSavedSecuredPartySearchList;
				savedSecuredPartySearchLists.value = [savedList, ...savedSecuredPartySearchLists.value.filter((list) => list.record_id !== savedList.record_id)];
			} else {
				const savedList = json.data as UccSavedDebtorSearchList;
				savedDebtorSearchLists.value = [savedList, ...savedDebtorSearchLists.value.filter((list) => list.record_id !== savedList.record_id)];
			}
			toast.success(`Saved ${uploadFieldLabel} list uploaded.`);
		} catch (error: any) {
			console.error(error);
			toast.error(error?.message || "Unable to upload saved list.");
		} finally {
			savedSearchUploadLoading.value = false;
		}
	}

	function downloadCsvFile(contents: string, filename: string) {
		const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
		const downloadUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(downloadUrl);
	}

	async function downloadSavedSearchList(value: string) {
		const [listId, field] = value.split("::");
		if (!listId || !field || savedSearchDownloadLoadingId.value) return;

		const isSecuredPartyMode = uccSearchMode.value === "secured_party";
		const savedList = isSecuredPartyMode ? savedSecuredPartySearchLists.value.find((list) => list.record_id === listId) : savedDebtorSearchLists.value.find((list) => list.record_id === listId);
		if (!savedList) return;

		savedSearchDownloadLoadingId.value = value;
		try {
			if (isSecuredPartyMode && !isSavedSecuredPartySearchField(field)) {
				throw new Error("This saved secured-party list field cannot be downloaded.");
			}
			if (!isSecuredPartyMode && !isSavedDebtorSearchField(field)) {
				throw new Error("This saved debtor list field cannot be downloaded.");
			}

			const valuesByField = isSecuredPartyMode ? await fetchSavedSecuredPartySearchListValues(listId) : await fetchSavedDebtorSearchListValues(listId);
			const values = valuesByField[field as SavedSecuredPartySearchField | SavedDebtorSearchField] || [];
			if (!values.length) {
				throw new Error("This saved list has no values to download.");
			}

			const fieldLabel = isSecuredPartyMode ? securedPartySearchFieldLabels[field as SavedSecuredPartySearchField] : debtorSearchFieldLabels[field as SavedDebtorSearchField];
			downloadCsvFile(buildSavedSearchListCsv(fieldLabel, values), buildSavedSearchListFilename(savedList.name));
		} catch (error: any) {
			console.error(error);
			toast.error(error?.message || "Unable to download saved list.");
		} finally {
			savedSearchDownloadLoadingId.value = null;
		}
	}

	function deleteSavedSearchList(value: string) {
		const [listId] = value.split("::");
		if (!listId || savedSearchDeleteLoadingId.value) return;
		const isSecuredPartyMode = uccSearchMode.value === "secured_party";
		const savedList = isSecuredPartyMode ? savedSecuredPartySearchLists.value.find((list) => list.record_id === listId) : savedDebtorSearchLists.value.find((list) => list.record_id === listId);
		if (!savedList) return;

		showModal({
			title: `Delete saved list "${savedList.name}"?`,
			message: `This will permanently remove this saved ${isSecuredPartyMode ? "secured-party" : "debtor"} search list and cannot be undone.`,
			confirmText: "Delete",
			cancelText: "Cancel",
			isDanger: true,
			onConfirm: async () => {
				savedSearchDeleteLoadingId.value = listId;
				try {
					const endpoint = isSecuredPartyMode ? "/ucc/secured_party/saved-search-lists" : "/ucc/debtor/saved-search-lists";
					const response = await fetch(`${getHostUrl()}${endpoint}/${encodeURIComponent(listId)}`, {
						method: "DELETE",
						headers: authHeaders(),
					});
					const json = await response.json().catch(() => ({}));
					if (!response.ok || !json?.success) {
						throw new Error(extractApiErrorMessage(json, `Unable to delete saved ${isSecuredPartyMode ? "secured-party" : "debtor"} list.`));
					}

					if (isSecuredPartyMode) {
						savedSecuredPartySearchLists.value = savedSecuredPartySearchLists.value.filter((list) => list.record_id !== listId);
						if (uccSavedSecuredPartySearchSelection.value?.list_id === listId) {
							uccSavedSecuredPartySearchSelection.value = null;
						}
					} else {
						savedDebtorSearchLists.value = savedDebtorSearchLists.value.filter((list) => list.record_id !== listId);
						if (uccSavedDebtorSearchSelection.value?.list_id === listId) {
							uccSavedDebtorSearchSelection.value = null;
						}
					}
					toast.success(`Saved ${isSecuredPartyMode ? "secured-party" : "debtor"} search list deleted.`);
				} catch (error: any) {
					console.error(error);
					toast.error(error?.message || "Unable to delete saved list.");
				} finally {
					savedSearchDeleteLoadingId.value = null;
				}
			},
		});
	}

	const searchValue = computed({
		get() {
			if (showCompaniesSearch.value) return companySearchValue.value;
			if (showContactsSearch.value) return contactSearchValue.value;
			if (showUccSearch.value) return uccSearchValue.value;
			return "";
		},
		set(value: string) {
			if (showCompaniesSearch.value) {
				companySearchValue.value = value;
			} else if (showContactsSearch.value) {
				contactSearchValue.value = value;
			} else if (showUccSearch.value) {
				uccSearchValue.value = value;
			}
		},
	});

	watch(
		() => route.query.value,
		(value) => {
			const nextValue = (value as string) || "";

			if (showCompaniesSearch.value) {
				companySearchValues.value = parseQuerySearchValues(value as string | string[] | undefined, true);
				companySearchValue.value = "";
			} else if (showContactsSearch.value) {
				contactSearchValue.value = nextValue;
			} else if (showUccSearch.value) {
				if (value === undefined && route.query.field === undefined && uccLastSearchPayload.value !== null) {
					return;
				}
				uccSearchValues.value = parseQuerySearchValues(value as string | string[] | undefined);
				uccSearchValue.value = "";
			}
		},
		{ immediate: true }
	);

	watch(
		() => route.query.field,
		(field) => {
			if (!showUccSearch.value) return;
			if (field === undefined && route.query.value === undefined && uccLastSearchPayload.value !== null) {
				return;
			}
			const nextField = (field as string) || "name";
			uccSearchField.value = nextField;
		},
		{ immediate: true }
	);

	watch(
		[uccSearchMode, uccSearchField],
		([mode, field]) => {
			const fields = (mode === "company" ? uccCompanyHeaderOptions.map((option) => option.value) : mode === "secured_party" ? securedPartySearchFields : debtorSearchFields) as readonly string[];
			if (!fields.includes(field)) {
				uccSearchField.value = "name";
			}
			syncUccDropdownOptions();
		},
		{ immediate: true }
	);

	watch(
		[showCompaniesSearch, () => user.value?.access_companies],
		([visible, hasAccess]) => {
			if (!visible || !process.client) return;
			if (hasAccess !== true) return;
			void loadCompanySearchHistory();
		},
		{ immediate: true }
	);

	watch(
		showUccSearch,
		(visible) => {
			if (!visible || !process.client) return;
			if (user.value?.access_ucc !== true) return;
			void loadUccSearchHistory();
		},
		{ immediate: true }
	);

	// A new Companies search just ran; make the next dropdown open refetch fresh history.
	watch(companySearchResultId, () => {
		companySearchHistoryLoadedAt.value = 0;
	});

	// A new search just ran; make the next dropdown open refetch fresh history.
	watch(uccLastSearchPayload, () => {
		uccSearchHistoryLoadedAt.value = 0;
	});

	const companyCreditsAvailable = computed(() => {
		const credits = user.value?.credits;
		return credits === undefined || credits === null ? true : credits > 0;
	});

	const canAccessCompanies = computed(() => {
		return user.value?.access_companies === true;
	});

	const creditItems = computed(() => {
		const items = [
			...(canAccessCompanies.value ? [{ key: "company", label: "Company", value: user.value?.credits }] : []),
			...(user.value?.access_ucc ? [{ key: "ucc", label: "UCC", value: user.value?.credits_ucc }] : []),
			{ key: "email", label: "Email", value: user.value?.credits_email },
			{ key: "email-validation", label: "Validation", value: user.value?.credits_email_validation },
			{ key: "mobile", label: "Mobile", value: user.value?.credits_mobile },
		];

		return items.map((item) => ({
			...item,
			display: item.value?.toLocaleString?.() ?? "",
		}));
	});

	// Balances are spent per-surface, so only the relevant ones stay inline.
	const contextualCreditKeys = computed<string[]>(() => {
		if (showCompaniesSearch.value) return canAccessCompanies.value ? ["company"] : [];
		if (route.path.startsWith("/ucc")) return user.value?.access_ucc ? ["ucc"] : [];
		if (route.path.includes("enrichment")) return ["email", "mobile", "email-validation"];
		return [];
	});

	const contextualCreditItems = computed(() => {
		return contextualCreditKeys.value.flatMap((key) => {
			const item = creditItems.value.find((entry) => entry.key === key);
			return item ? [item] : [];
		});
	});

	// Long enough that passing over the pill on the way to the account menu
	// doesn't trigger it, short enough to feel immediate when aimed at.
	const CREDITS_HOVER_OPEN_DELAY = 120;
	const CREDITS_HOVER_CLOSE_DELAY = 200;

	const creditsHolder = ref<HTMLElement | null>(null);
	const creditsOpen = ref<boolean>(false);
	// A click pins the panel so it survives the pointer leaving.
	const creditsPinned = ref<boolean>(false);
	const creditsHoverSuppressed = ref<boolean>(false);
	let creditsHoverTimer: ReturnType<typeof setTimeout> | null = null;

	function clearCreditsHoverTimer() {
		if (creditsHoverTimer === null) return;
		clearTimeout(creditsHoverTimer);
		creditsHoverTimer = null;
	}

	function openCredits() {
		clearCreditsHoverTimer();
		if (creditsOpen.value) return;
		creditsOpen.value = true;
		document.addEventListener("click", onCreditsClickOutside);
		document.addEventListener("keydown", onCreditsKeydown);
	}

	function closeCredits() {
		clearCreditsHoverTimer();
		creditsPinned.value = false;
		if (!creditsOpen.value) return;
		creditsOpen.value = false;
		document.removeEventListener("click", onCreditsClickOutside);
		document.removeEventListener("keydown", onCreditsKeydown);
	}

	function onCreditsPointerEnter() {
		clearCreditsHoverTimer();
		if (creditsHoverSuppressed.value || creditsOpen.value) return;
		creditsHoverTimer = setTimeout(openCredits, CREDITS_HOVER_OPEN_DELAY);
	}

	function onCreditsPointerLeave() {
		clearCreditsHoverTimer();
		creditsHoverSuppressed.value = false;
		if (creditsPinned.value) return;
		creditsHoverTimer = setTimeout(closeCredits, CREDITS_HOVER_CLOSE_DELAY);
	}

	function toggleCredits() {
		if (creditsPinned.value) {
			// Dismissing by click must stick while the pointer is still on the
			// pill, otherwise hover would immediately reopen it.
			creditsHoverSuppressed.value = true;
			closeCredits();
			return;
		}
		openCredits();
		creditsPinned.value = true;
	}

	function onCreditsClickOutside(event: MouseEvent) {
		if (!creditsHolder.value) return;
		if (creditsHolder.value.contains(event.target as Node)) return;
		closeCredits();
	}

	function onCreditsKeydown(event: KeyboardEvent) {
		if (event.key === "Escape") closeCredits();
	}

	watch(() => route.fullPath, closeCredits);

	onUnmounted(() => {
		clearCreditsHoverTimer();
		document.removeEventListener("click", onCreditsClickOutside);
		document.removeEventListener("keydown", onCreditsKeydown);
	});

	const accountMenuSections = computed<AccountMenuSection[]>(() => {
		return getAccountMenuSections(user.value);
	});

	const isCompanySearchDisabled = computed(() => {
		return showCompaniesSearch.value && companyCreditsAvailable.value === false;
	});

	function runCompanySearch(payload?: SearchSubmitPayload) {
		if (!showCompaniesSearch.value || isCompanySearchDisabled.value) return;

		const values = collectCompanySearchValues(payload);
		const nextQuery: Record<string, string> = {};

		if (route.query.query) {
			nextQuery.query = route.query.query as string;
		}

		if (values.length > 0) {
			nextQuery.value = serializeCompanySearchValues(values);
		}

		if (companyCompetitorLookalike.value) {
			nextQuery.competitor_lookalike = "true";
		}

		router.push({
			path: "/companies",
			query: Object.keys(nextQuery).length ? nextQuery : undefined,
		});

		const sequence = companyHeaderSearchSubmitSequence.value + 1;
		companyHeaderSearchSubmitSequence.value = sequence;
		companyHeaderSearchSubmit.value = {
			sequence,
			query: nextQuery.query,
			value: nextQuery.value,
			competitor_lookalike: nextQuery.competitor_lookalike,
			values,
			submittedAt: Date.now(),
		};

		if (values.length > 0) {
			companySearchValues.value = values;
			companySearchValue.value = "";
		}
	}

	const companySearchModes = [
		{
			value: "direct",
			label: "Keyword search",
			description: "Match your keywords across company websites, industries, SIC & NAICS codes, and more.",
			placeholder: "Search by keyword, industry, and SIC/NAICS code.",
		},
		{
			value: "lookalike",
			label: "Lookalike search",
			description: "Find companies that resemble the ones you search for. Requires a company website as input.",
			placeholder: "Find companies similar to a website you enter.",
		},
	];

	const companySearchMode = computed(() => (companyCompetitorLookalike.value ? "lookalike" : "direct"));

	const companySearchPlaceholder = computed(() => {
		if (isPhone.value) return "Search…";
		const active = companySearchModes.find((mode) => mode.value === companySearchMode.value);
		return active?.placeholder ?? "Describe the companies you need…";
	});

	function onCompanySearchModeSelected(value: string) {
		companyCompetitorLookalike.value = value === "lookalike";

		// Only re-run when a search is actually active, so switching modes on an
		// idle page doesn't flash the loading state for an empty result set.
		const hasActiveSearch = Boolean(route.query.query || route.query.value) || companySearchValues.value.length > 0;
		if (hasActiveSearch) {
			runCompanySearch();
		}
	}

	function runContactSearch() {
		if (!showContactsSearch.value) return;

		const trimmed = searchValue.value?.trim() || "";
		const nextQuery: Record<string, string> = {};

		if (route.query.query) {
			nextQuery.query = route.query.query as string;
		}

		if (trimmed.length > 0) {
			nextQuery.value = trimmed;
		}

		router.push({
			path: "/contacts",
			query: Object.keys(nextQuery).length ? nextQuery : undefined,
		});
	}

	function runUccSearch(payload?: SearchSubmitPayload) {
		if (!showUccSearch.value) return;

		const values = collectUccSearchValues(payload);
		const selectedState = normalizeUccSuggestionState(payload?.state);
		if (values.length > UCC_HEADER_SEARCH_LIMIT) {
			onUccSearchLimitReached({ attemptedCount: values.length, maxValues: UCC_HEADER_SEARCH_LIMIT });
			return;
		}

		uccSavedDebtorSearchSelection.value = null;
		uccSavedSecuredPartySearchSelection.value = null;
		const nextQuery: Record<string, string> = {};

		const selectedField = uccSearchField.value || "name";
		if (uccSearchMode.value === "company") nextQuery.mode = "company";
		if (selectedField) {
			nextQuery.field = selectedField;
		}

		if (values.length > 0) {
			nextQuery.value = values.length === 1 ? values[0] : JSON.stringify(values);
		}
		if (selectedState) {
			nextQuery.state = selectedState;
		}

		if (values.length > 0) {
			uccSearchValues.value = values;
			uccSearchValue.value = "";
		}

		const sequence = uccHeaderSearchSubmitSequence.value + 1;
		uccHeaderSearchSubmitSequence.value = sequence;
		uccHeaderSearchSubmit.value = {
			sequence,
			mode: uccSearchMode.value,
			field: selectedField,
			values,
			...(selectedState ? { state: selectedState } : {}),
			submittedAt: Date.now(),
		};

		router.push({
			path: "/ucc",
			query: Object.keys(nextQuery).length ? nextQuery : undefined,
		});
	}

	function onUccSearchLimitReached({ maxValues }: { attemptedCount: number; maxValues: number }) {
		toast(`Search supports up to ${maxValues} entries. For larger lists, use Bulk Enrichment.`, {
			action: {
				label: "Open Bulk Enrichment",
				onClick: () => void router.push({ path: "/enrichment", query: { view: "bulk" } }),
			},
		});
	}

	function onUccDropdownSelect(value: string) {
		uccSearchField.value = value;
		uccSavedDebtorSearchSelection.value = null;
		uccSavedSecuredPartySearchSelection.value = null;
	}

	async function onSavedSecuredPartyDropdownSelect(value: string) {
		const [listId, field] = value.split("::");
		if (!listId || !field || !isSavedSecuredPartySearchField(field)) return;
		const savedList = savedSecuredPartySearchLists.value.find((list) => list.record_id === listId);
		if (!savedList) return;
		const count = Number(savedList.value_counts?.[field] ?? 0);
		if (count <= 0) return;

		let valuesByField: Partial<Record<SavedSecuredPartySearchField, string[]>> = savedList.values_by_field || {};
		try {
			valuesByField = await fetchSavedSecuredPartySearchListValues(listId);
		} catch (error: any) {
			console.error(error);
			toast.error(error?.message || "Unable to load saved secured-party list values.");
			return;
		}

		uccSearchMode.value = "secured_party";
		uccSearchField.value = field;
		uccSearchValue.value = "";
		uccSearchValues.value = [];
		uccSavedSecuredPartySearchSelection.value = {
			list_id: savedList.record_id,
			list_name: savedList.name,
			field,
			field_label: securedPartySearchFieldLabels[field],
			value_count: count,
			values_by_field: valuesByField,
			selected_at: Date.now(),
		};
	}

	async function onSavedDebtorDropdownSelect(value: string) {
		const [listId, field] = value.split("::");
		if (!listId || !field || !isSavedDebtorSearchField(field)) return;
		const savedList = savedDebtorSearchLists.value.find((list) => list.record_id === listId);
		if (!savedList) return;
		const count = Number(savedList.value_counts?.[field] ?? 0);
		if (count <= 0) return;

		let valuesByField: Partial<Record<SavedDebtorSearchField, string[]>> = savedList.values_by_field || {};
		try {
			valuesByField = await fetchSavedDebtorSearchListValues(listId);
		} catch (error: any) {
			console.error(error);
			toast.error(error?.message || "Unable to load saved debtor list values.");
			return;
		}

		uccSearchMode.value = "debtor";
		uccSearchField.value = field;
		uccSearchValue.value = "";
		uccSearchValues.value = [];
		uccSavedDebtorSearchSelection.value = {
			list_id: savedList.record_id,
			list_name: savedList.name,
			field,
			field_label: debtorSearchFieldLabels[field],
			value_count: count,
			values_by_field: valuesByField,
			selected_at: Date.now(),
		};
	}

	function onSavedDropdownSelect(value: string) {
		if (uccSearchMode.value === "secured_party") {
			void onSavedSecuredPartyDropdownSelect(value);
			return;
		}
		void onSavedDebtorDropdownSelect(value);
	}

	function clearSavedSearchSelection() {
		uccSavedDebtorSearchSelection.value = null;
		uccSavedSecuredPartySearchSelection.value = null;
	}

	watch(
		[showUccSearch, uccSearchMode],
		([isUccSearchVisible, mode]) => {
			if (isUccSearchVisible && mode === "secured_party") {
				void fetchSavedSecuredPartySearchLists();
			} else if (isUccSearchVisible && mode === "debtor") {
				void fetchSavedDebtorSearchLists();
			}
		},
		{ immediate: true }
	);
</script>

<template>
	<header
		class="header"
		:class="[
			{
				'with-search': showCompaniesSearch || showContactsSearch || showUccSearch,
				'recommendations-header': route.path.startsWith('/ucc/recommendations'),
				'secured-party-overview-header': route.path.startsWith('/ucc/secured-party/'),
			},
		]"
	>
		<div class="title-area" v-if="!showCompaniesSearch && !showContactsSearch && !showUccSearch">
			<h1>{{ title }}</h1>
		</div>

		<div class="search-area" v-else-if="showCompaniesSearch">
			<CompanySearchInput
				class="header-search"
				v-model:value="searchValue"
				v-model:values="companySearchValues"
				:placeholder="companySearchPlaceholder"
				:dropdown-options="[]"
				:history-options="companyHistoryDropdownOptions"
				:history-dropdown-label="companyHistoryPastThreeDays ? 'Past 3 days' : 'Recent searches'"
				history-delete-label="Remove from history"
				history-clear-label="Clear"
				:history-loading="companySearchHistoryLoading"
				:history-action-label="companyHistoryPastThreeDays ? '' : 'Show older results'"
				:history-empty-label="companyHistoryPastThreeDays ? 'No searches in the past 3 days' : 'No recent searches'"
				:open-on-focus="true"
				:show-dropdown="true"
				:allow-multiple="true"
				:split-or-values="true"
				:show-submit-button="true"
				:search-modes="companySearchModes"
				:active-search-mode="companySearchMode"
				@mode:selected="onCompanySearchModeSelected"
				@enterpressed="runCompanySearch"
				@focusin="onCompanySearchFocus"
				@dropdown:history-selected="onCompanyHistorySelect"
				@dropdown:history-delete="onCompanyHistoryDelete"
				@dropdown:history-clear="onCompanyHistoryClear"
				@dropdown:history-action="changeCompanyHistoryRange"
				:disabled="isCompanySearchDisabled"
			/>
		</div>

		<div class="search-area" v-else-if="showContactsSearch">
			<SearchInput class="header-search" v-model:value="searchValue" placeholder="Search contacts…" @enterpressed="runContactSearch" @change="runContactSearch" />
		</div>

		<div class="search-area" v-else-if="showUccSearch">
			<input
				ref="savedSearchUploadInput"
				class="saved-search-upload-input"
				type="file"
				accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				@change="uploadSavedSearchFile"
			/>
			<CompanySearchInput
				class="header-search ucc-header-search"
				v-model:value="searchValue"
				v-model:values="uccSearchValues"
				:placeholder="uccSearchPlaceholder"
				:dropdown-options="uccDropdownOptions"
				:secondary-dropdown-options="savedSearchDropdownOptions"
				secondary-dropdown-label="Saved lists"
				secondary-dropdown-download-label="Download saved list"
				secondary-dropdown-delete-label="Delete saved list"
				:history-options="uccSearchMode === 'company' ? [] : uccHistoryDropdownOptions"
				:history-dropdown-label="uccHistoryPastThreeDays ? 'Past 3 days' : 'Recent searches'"
				history-delete-label="Remove from history"
				history-clear-label="Clear"
				:history-loading="uccSearchHistoryLoading"
				:history-action-label="uccSearchMode === 'company' || uccHistoryPastThreeDays ? '' : 'Show older results'"
				:history-empty-label="uccHistoryPastThreeDays ? 'No searches in the past 3 days' : 'No recent searches'"
				:suggestion-options="uccSuggestionDropdownOptions"
				:suggestions-enabled="uccSuggestionsEnabled"
				:suggestions-loading="uccSuggestionsLoading"
				suggestion-dropdown-label="Matches"
				:suggestion-empty-label="uccSuggestionEmptyLabel"
				:open-on-focus="true"
				:dropdown-action-label="uccSearchMode === 'company' ? '' : savedSearchUploadActionLabel"
				:values-label="savedSearchValuesLabel"
				:show-dropdown="true"
				:single-select-dropdown="true"
				:allow-multiple="true"
				:max-values="UCC_HEADER_SEARCH_LIMIT"
				:show-submit-button="true"
				@enterpressed="runUccSearch"
				@values-limit-reached="onUccSearchLimitReached"
				@focusin="onUccSearchFocus"
				@dropdown:selected="onUccDropdownSelect"
				@dropdown:secondary-selected="onSavedDropdownSelect"
				@dropdown:secondary-download="downloadSavedSearchList"
				@dropdown:secondary-delete="deleteSavedSearchList"
				@dropdown:history-selected="onUccHistorySelect"
				@dropdown:history-delete="onUccHistoryDelete"
				@dropdown:history-clear="onUccHistoryClear"
				@dropdown:history-action="changeUccHistoryRange"
				@dropdown:suggestion-selected="onUccSuggestionSelect"
				@dropdown:action="triggerSavedSearchUpload"
				@values-label:cleared="clearSavedSearchSelection"
			/>
		</div>

		<div class="profile">
			<div class="credits-inline">
				<template v-for="credit in contextualCreditItems" :key="credit.key">
					<div class="credit contextual">
						<span class="label">{{ credit.label }}</span>
						<span class="value">{{ credit.display }}</span>
					</div>
					<span class="dot"></span>
				</template>

				<div class="credits-menu" ref="creditsHolder" @mouseenter="onCreditsPointerEnter" @mouseleave="onCreditsPointerLeave">
					<button type="button" class="credits-pill" :class="{ open: creditsOpen }" aria-haspopup="true" :aria-expanded="creditsOpen" title="View all credit balances" @click="toggleCredits">
						<span class="pill-label">Credits</span>
						<icon-vertical-arrow size="small" color="black" />
					</button>

					<div class="credits-panel shadow-normal border-grey" v-if="creditsOpen">
						<span class="panel-heading">Credit balances</span>
						<ul class="panel-list">
							<li v-for="credit in creditItems" :key="credit.key">
								<span class="label">{{ credit.label }}</span>
								<span class="value">{{ credit.display }}</span>
							</li>
						</ul>
					</div>
				</div>
			</div>
			<hr />
			<div class="button" role="button" tabindex="0" aria-label="Account menu" @mouseenter="closeCredits" @click="onProfileClick" @keydown.enter.prevent="onProfileClick">
				<h4>{{ user?.first_name }} {{ user?.last_name }}</h4>

				<profile-picture v-if="hasProfilePicture" size="small" :url="user?.profile_picture" :name="user!.first_name + ' ' + user!.last_name"></profile-picture>
				<!-- Phone-only monogram: the name is hidden there, and without a picture
				     the account tap target would otherwise be empty. -->
				<profile-picture v-else size="small" class="mobile-avatar" :name="userFullName"></profile-picture>

				<ul class="shadow-normal border-grey">
					<template v-for="section in accountMenuSections" :key="section.id">
						<li class="menu-section-label">
							<span>{{ section.label }}</span>
						</li>
						<li class="menu-link" v-for="item in section.items" :key="item.id">
							<NuxtLink v-if="'to' in item" :to="item.to">
								<h3>{{ item.label }}</h3>
							</NuxtLink>
							<a v-else :href="item.href">
								<h3>{{ item.label }}</h3>
							</a>
						</li>
					</template>
					<template v-if="layoutPreference.forcedDesktop.value">
						<li class="menu-section-label">
							<span>Display</span>
						</li>
						<li class="menu-link">
							<button type="button" class="menu-action" @click="layoutPreference.useMobileLayout()">
								<h3>Switch to mobile site</h3>
							</button>
						</li>
					</template>
				</ul>
			</div>
		</div>
	</header>
</template>

<style scoped lang="scss">
	.mobile-avatar {
		display: none;
	}

	.header {
		display: flex;
		flex-direction: row;
		background: var(--bg-surface);
		width: 100%;
		min-height: var(--header-height);
		height: auto;
		align-items: center;
		justify-content: space-between;
		border-bottom: 1px solid var(--border);
		padding: 8px var(--page-padding);
		box-sizing: border-box;
		gap: 16px;

		.title-area {
			flex: 1;
			min-width: 240px;

			// Page titles sit inside a 56px bar, so they take the h3 scale rather
			// than the display size the h1 element would otherwise bring.
			h1 {
				font-size: 18px;
				line-height: 24px;
				letter-spacing: -0.3px;
			}
		}

		.search-area {
			flex: 1;
			/* Keeps the submit button within easy reach of the input's centre
			   rather than pushed out to the far edge on wide screens. */
			max-width: 860px;
			min-width: 320px;
			width: 100%;
			min-height: 38px;
			display: flex;
			align-items: center;

			.saved-search-upload-input {
				display: none;
			}

			.header-search {
				margin-bottom: 0;
			}

			// A white field with a visible edge, so the grey chips inside it stay
			// legible; the border firms up on hover and goes dark on focus.
			:deep(.input-surface) {
				height: 38px;
				border-color: var(--border);
				border-radius: var(--radius-md);
				background: var(--bg-surface);
				transition: border-color var(--dur-fast) var(--ease-out);
			}

			:deep(.input-content) {
				height: 38px;
			}

			// Multi-value header searches grow by complete chip rows instead of
			// hiding later criteria in a horizontal scroller.
			:deep(.header-search.has-submit-button .input-surface) {
				height: auto;
				min-height: 38px;
				padding-top: 6px;
				padding-bottom: 6px;
			}

			:deep(.header-search.has-submit-button .input-content) {
				align-items: center;
				flex-wrap: wrap;
				height: auto;
				min-height: 24px;
				overflow: visible;
			}

			:deep(.header-search.has-submit-button .value-chips) {
				display: contents;
			}

			:deep(.header-search:hover .input-surface) {
				border-color: var(--border-strong);
			}

			:deep(.header-search:focus-within .input-surface) {
				border-color: var(--text);
			}

			:deep(.search-input input[type="text"]) {
				font-size: 14px;
				min-height: 38px;
				line-height: 38px;
			}

			:deep(.header-search.has-submit-button input[type="text"]) {
				height: 24px;
				min-height: 24px;
				line-height: 24px;
			}

			// Once criteria exist, keep only a compact typing target at the end of
			// the row. The shared input otherwise reserves 160px and wraps while the
			// visible chips still have room on the current line.
			:deep(.header-search.has-submit-button.has-content input[type="text"]) {
				flex: 1 1 64px;
				min-width: 64px;
			}

			:deep(.header-search.has-submit-button.has-content input[type="text"]::placeholder) {
				opacity: 0;
			}

			:deep(.ucc-header-search .search-input input[type="text"]::placeholder) {
				color: $black;
				opacity: 1;
			}
		}

		h1 {
			user-select: none;
			/* .dark & {
				color: $dark_mode_color;
			} */
		}

		&.with-search {
			flex-wrap: wrap;
		}

		.profile {
			display: flex;
			flex-direction: row;
			align-items: center;
			height: 100%;
			flex-shrink: 0;
			gap: 12px;

			.credits-inline {
				display: inline-flex;
				align-items: center;
				gap: 6px;
				padding: 0;

				.credits-menu {
					position: relative;
					display: inline-flex;
					align-items: center;
				}
				.credit {
					display: flex;
					align-items: baseline;
					gap: 5px;
					// Label and value are a pair, so the label only steps down one size
					// and one contrast level. 11px muted is the caption size used for
					// section headings elsewhere, and it read as a footnote here.
					.label {
						font-size: 12px;
						color: var(--text-secondary);
						text-transform: uppercase;
						letter-spacing: 0.03em;
						@include FontWeightMedium(false);
					}
					.value {
						font-size: 14px;
						@include FontWeightMedium(false);
						color: var(--text);
						font-variant-numeric: tabular-nums;
					}
				}
				.dot {
					width: 3px;
					height: 3px;
					border-radius: 50%;
					background: var(--border-strong);
					flex-shrink: 0;
				}

				.credits-pill {
					display: inline-flex;
					align-items: center;
					gap: 6px;
					height: 30px;
					padding: 0 10px;
					border: 1px solid var(--border);
					border-radius: var(--radius-md);
					background: var(--bg-surface);
					cursor: pointer;
					transition: all 0.2s ease-in-out;
					flex-shrink: 0;

					.pill-label {
						font-size: 12px;
						color: var(--text);
						text-transform: uppercase;
						letter-spacing: 0.01em;
						@include FontWeightMedium(false);
					}

					:deep(.icon-vertical-arrow) {
						transition: transform 0.2s ease-in-out;
					}

					&:hover,
					&:focus-visible {
						background: var(--bg-subtle);
						border-color: var(--border-strong);
					}

					&.open {
						background: var(--bg-subtle);
						:deep(.icon-vertical-arrow) {
							transform: rotate(180deg);
						}
					}
				}

				.credits-panel {
					position: absolute;
					top: calc(100% + 10px);
					right: 0;
					z-index: 20;
					width: 260px;
					padding: 12px 0 8px;
					border-radius: var(--radius-lg);
					background: white;
					display: flex;
					flex-direction: column;

					/* Hover bridge so crossing the gap from the pill to the
					   panel doesn't register as leaving the menu. */
					&::before {
						content: "";
						position: absolute;
						top: -10px;
						left: 0;
						right: 0;
						height: 10px;
					}

					.panel-heading {
						display: block;
						padding: 0 16px 8px;
						font-size: 11px;
						text-transform: uppercase;
						letter-spacing: 0.06em;
						color: #7d7d7d;
						@include FontWeightMedium(false);
					}

					.panel-list {
						list-style: none;
						display: flex;
						flex-direction: column;

						li {
							display: flex;
							align-items: baseline;
							justify-content: space-between;
							gap: 12px;
							padding: 7px 16px;

							.label {
								font-size: 13px;
								color: $black;
							}

							.value {
								font-size: 15px;
								color: $black;
								@include FontWeightMedium(false);
								font-variant-numeric: tabular-nums;
							}
						}
					}
				}
			}

			h4 {
				user-select: none;
				font-size: 14px;
				letter-spacing: -0.1px;
			}

			:deep(.profile-picture.small) {
				width: 30px;
				height: 30px;
				border-radius: 50%;

				h2 {
					font-size: 12px;
					letter-spacing: 0;
				}
			}

			hr {
				height: 20px;
				border-right: 1px solid var(--border);
				border-left: none;
				margin: 0 6px 0 2px;
				/* .dark & {
					color: $dark_mode_color;
				} */
			}

			.button {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: center;
				height: 100%;
				padding: 0 15px;
				margin-right: -15px;
				position: relative;
				gap: 10px;
				cursor: pointer;

				ul {
					right: 0;
					list-style: none;
					position: absolute;
					top: 100%;
					width: 240px;
					overflow: hidden;
					border-radius: var(--radius-lg);
					background: var(--bg-surface);
					display: flex;
					flex-direction: column;
					padding: 6px;
					max-height: min(80vh, 640px);
					overflow-y: auto;
					pointer-events: none;
					visibility: hidden;
					opacity: 0;
					transition: all 0.2s ease-in-out;
					transform: translateY(-5px);
					/* Hover bridge to remove gaps between trigger and menu */
					&::before {
						content: "";
						position: absolute;
						top: -12px;
						left: 0;
						right: 0;
						height: 12px;
					}

					li.menu-link {
						.menu-action {
							width: 100%;
							border: 0;
							background: transparent;
							text-align: left;
							cursor: pointer;
						}

						a,
						.menu-action {
							display: flex;
							min-height: 36px;
							padding: 8px 10px;
							align-items: center;
							border-radius: var(--radius-md);
							transition: background var(--dur-fast) var(--ease-out);

							h3 {
								font-size: 14px;
								letter-spacing: -0.1px;
							}
						}
						a:hover,
						.menu-action:hover {
							background: var(--bg-subtle);
						}
						a.router-link-exact-active {
							background: var(--accent-soft);
							h3 {
								color: var(--accent);
							}
						}
					}

					li.menu-section-label {
						padding: 10px 10px 4px 10px;
						cursor: default;
						user-select: none;

						span {
							display: block;
							font-size: 11px;
							text-transform: uppercase;
							letter-spacing: 0.06em;
							color: #7d7d7d;
							@include FontWeightMedium(false);
						}
					}

					li.menu-section-label:not(:first-child) {
						margin-top: 4px;
						border-top: 1px solid var(--border);
					}
				}
			}

			.button:hover {
				ul {
					pointer-events: all;
					opacity: 1;
					visibility: visible;
					transform: translateY(0px);
				}
			}

			/* Keep dropdown open when hovering over the menu itself */
			ul:hover,
			.button:focus-within ul {
				pointer-events: all;
				opacity: 1;
				visibility: visible;
				transform: translateY(0px);
			}
		}
	}
	/* Phone: one 52px bar, no wrapping. Brand mark, then the page title or the
	   search field, then the avatar. Credits and the account name move into the
	   menu sheet, and the hover dropdown is disabled outright so a tap on the
	   avatar opens the sheet instead of pinning a hover menu open. The old
	   layout wrapped the profile row under the search field and let it spill
	   over the page below (the overlapping "Filters" row in the bug report). */
	@include phone {
		.header {
			flex-wrap: nowrap;
			gap: 10px;
			// Same gutter as the page below, so the search field's left edge lines
			// up with the Filters button and the results.
			padding: 6px var(--page-padding);
			padding-left: max(var(--page-padding), var(--safe-left));
			padding-right: max(var(--page-padding), var(--safe-right));

			&.with-search {
				flex-wrap: nowrap;
			}

			.title-area {
				flex: 1 1 auto;
				min-width: 0;

				h1 {
					font-size: 17px;
					line-height: 22px;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
			}

			// One 40px row like the Filters button beside it below: the mode chip
			// and the typing target share the line instead of the chip taking a
			// row of its own, and the icon/submit gutters tighten to phone widths.
			// Added search terms still wrap onto new rows.
			.search-area {
				flex: 1 1 auto;
				min-width: 0;
				max-width: none;
				width: auto;

				:deep(.header-search .icon-container) {
					left: 12px;
				}

				:deep(.header-search.has-submit-button .input-surface) {
					min-height: 40px;
					padding: 4px 42px 4px 50px;
				}

				:deep(.header-search .input-content) {
					gap: 6px;
				}

				:deep(.header-search .submit-button) {
					right: 4px;
				}

				:deep(.header-search.has-submit-button input[type="text"]) {
					flex: 1 1 72px;
					min-width: 72px;
					text-overflow: ellipsis;
				}

				:deep(.header-search .dropdown-selection-chip) {
					max-width: 140px;
					padding: 3px 9px;
				}
			}

			.profile {
				height: auto;
				gap: 0;

				.credits-inline,
				hr,
				h4 {
					display: none;
				}

				.mobile-avatar {
					display: flex;
				}

				.button {
					height: 40px;
					width: 40px;
					padding: 0;
					margin: 0;
					border-radius: 50%;
					-webkit-tap-highlight-color: transparent;

					ul {
						display: none;
					}
				}

				:deep(.profile-picture.small) {
					width: 32px;
					height: 32px;
				}
			}
		}
	}
</style>
